import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from './store';
import {
    ActivationStick,
    Device,
    SystemConfiguration,
    OnboardDeviceHardwareConfiguration,
    OnboardDevicesOnlinePeersResponse,
    OnboardDevicesPagination,
    OnboardDevicesQueryParams,
    OnboardDevicesResponse,
} from '../interfaces';
import { DEFAULT_ONBOARD_DEVICES_PAGE_SIZE, getOnboardDevices } from '../api/getOnboardDevices';
import { getActivationSticks } from '../api/getActivationSticks';
import { getSystemConfigurations } from '../api/getSystemConfigurations';
import { getOnboardDevicesHardwareConfigurations } from '../api/getOnboardDevicesHardwareConfigurations';
import { getOnboardDevicesOnlinePeers } from '../api/getOnboardDevicesOnlinePeers';

export interface DevicesState {
    onboardDevices: Device[] | null;
    onboardDevicesPagination: OnboardDevicesPagination;
    onboardDevicesLoading: boolean;
    onboardDevicesError: string | null;
    onboardDevicesCurrentRequestId: string | null;
    selectedOnboardDeviceTableRows: string[];
    activationSticks: ActivationStick[] | null;
    systemConfigurations: SystemConfiguration[] | null;
    onboardDevicesHardwareConfigurations: OnboardDeviceHardwareConfiguration[] | null;
    onboardDevicesOnlinePeers: OnboardDevicesOnlinePeersResponse | null;
    onboardDevicesOnlinePeersLoading: boolean;
    onboardDevicesOnlinePeersError: string | null;
    selectedOnboardHardwareConfigurationRows: string[];
}

const initialState: DevicesState = {
    selectedOnboardDeviceTableRows: [],
    onboardDevices: null,
    onboardDevicesPagination: {
        page: 1,
        pageSize: DEFAULT_ONBOARD_DEVICES_PAGE_SIZE,
        totalItems: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
    },
    onboardDevicesLoading: false,
    onboardDevicesError: null,
    onboardDevicesCurrentRequestId: null,
    activationSticks: null,
    systemConfigurations: null,
    onboardDevicesHardwareConfigurations: null,
    onboardDevicesOnlinePeers: null,
    onboardDevicesOnlinePeersLoading: false,
    onboardDevicesOnlinePeersError: null,
    selectedOnboardHardwareConfigurationRows: [],
};

export const loadOnboardDevices = createAsyncThunk<
    OnboardDevicesResponse,
    OnboardDevicesQueryParams | undefined
>('devices/loadOnboardDevices', async (params, thunkApi) => {
    return await getOnboardDevices(
        params || { page: 1, pageSize: DEFAULT_ONBOARD_DEVICES_PAGE_SIZE },
        thunkApi.signal,
    );
});

export const loadOnboardDevicesHardwareConfigurations = createAsyncThunk(
    'devices/loadOnboardDevicesHardwareConfigurations',
    async () => {
        return await getOnboardDevicesHardwareConfigurations();
    },
);

export const loadActivationSticks = createAsyncThunk('devices/loadActivationSticks', async () => {
    return await getActivationSticks();
});

export const loadOnboardDevicesOnlinePeers = createAsyncThunk(
    'devices/loadOnboardDevicesOnlinePeers',
    async () => {
        return await getOnboardDevicesOnlinePeers();
    },
);

export const loadSystemConfigurations = createAsyncThunk('devices/loadSystemConfigurations', async () => {
    return await getSystemConfigurations();
});

export const DevicesSlice = createSlice({
    name: 'devices',
    initialState,
    reducers: {
        setSelectedOnboardDeviceTableRows: (state, action: PayloadAction<string[]>) => {
            state.selectedOnboardDeviceTableRows = action.payload;
        },
        setSelectedOnboardHardwareConfigurationRows: (state, action: PayloadAction<string[]>) => {
            state.selectedOnboardHardwareConfigurationRows = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadOnboardDevices.pending, (state, action) => {
                state.onboardDevicesLoading = true;
                state.onboardDevicesError = null;
                state.onboardDevicesCurrentRequestId = action.meta.requestId;
            })
            .addCase(loadOnboardDevices.fulfilled, (state, action) => {
                if (state.onboardDevicesCurrentRequestId !== action.meta.requestId) {
                    return;
                }

                state.onboardDevices = action.payload.items;
                state.onboardDevicesPagination = action.payload.pagination;
                state.onboardDevicesLoading = false;
                state.onboardDevicesCurrentRequestId = null;
            })
            .addCase(loadOnboardDevices.rejected, (state, action) => {
                if (state.onboardDevicesCurrentRequestId !== action.meta.requestId) {
                    return;
                }

                state.onboardDevicesLoading = false;
                state.onboardDevicesCurrentRequestId = null;
                if (!action.meta.aborted) {
                    state.onboardDevicesError = action.error.message || 'Failed to load onboard devices.';
                    console.error(action.error);
                }
            })
            .addCase(loadActivationSticks.fulfilled, (state, action) => {
                state.activationSticks = action.payload;
            })
            .addCase(loadActivationSticks.rejected, (state, action) => {
                console.error(action.error);
            })
            .addCase(loadSystemConfigurations.fulfilled, (state, action) => {
                state.systemConfigurations = action.payload;
            })
            .addCase(loadOnboardDevicesHardwareConfigurations.fulfilled, (state, action) => {
                state.onboardDevicesHardwareConfigurations = action.payload;
            })
            .addCase(loadOnboardDevicesOnlinePeers.pending, (state) => {
                state.onboardDevicesOnlinePeersLoading = true;
                state.onboardDevicesOnlinePeersError = null;
            })
            .addCase(loadOnboardDevicesOnlinePeers.fulfilled, (state, action) => {
                state.onboardDevicesOnlinePeers = action.payload;
                state.onboardDevicesOnlinePeersLoading = false;
            })
            .addCase(loadOnboardDevicesOnlinePeers.rejected, (state, action) => {
                state.onboardDevicesOnlinePeersLoading = false;
                state.onboardDevicesOnlinePeersError = action.error.message || 'Failed to load online peers.';
                console.error(action.error);
            })
            .addCase(loadSystemConfigurations.rejected, (state, action) => {
                console.error(action.error);
            });
    },
});

export const { setSelectedOnboardDeviceTableRows, setSelectedOnboardHardwareConfigurationRows } =
    DevicesSlice.actions;

export const selectOnboardDevices = (state: RootState) => state.devices.onboardDevices;
export const selectOnboardDevicesPagination = (state: RootState) => state.devices.onboardDevicesPagination;
export const selectOnboardDevicesLoading = (state: RootState) => state.devices.onboardDevicesLoading;
export const selectOnboardDevicesError = (state: RootState) => state.devices.onboardDevicesError;
export const selectOnboardDevicesHardwareConfigurations = (state: RootState) =>
    state.devices.onboardDevicesHardwareConfigurations;
export const selectOnboardDevicesOnlinePeers = (state: RootState) => state.devices.onboardDevicesOnlinePeers;
export const selectOnboardDevicesOnlinePeersLoading = (state: RootState) =>
    state.devices.onboardDevicesOnlinePeersLoading;
export const selectOnboardDevicesOnlinePeersError = (state: RootState) =>
    state.devices.onboardDevicesOnlinePeersError;
export const selectSystemConfigurations = (state: RootState) => state.devices.systemConfigurations;
export const selectActivationSticks = (state: RootState) => state.devices.activationSticks;
export const selectSelectedOnboardDeviceTableRows = (state: RootState) =>
    state.devices.selectedOnboardDeviceTableRows;
export const selectSelectedOnboardHardwareConfigurationRows = (state: RootState) =>
    state.devices.selectedOnboardHardwareConfigurationRows;

export default DevicesSlice.reducer;
